import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const audiences = {
  posters: {
    title: "For customers",
    sub: "Stop chasing quotes. Start tracking your move.",
    img: "https://images.pexels.com/photos/4246197/pexels-photo-4246197.jpeg",
    imgAlt: "Family loading boxes for a house removal",
    points: [
      "Book house removals, waste clearance, or office moves in under 60 seconds.",
      "Funds sit in Stripe escrow until proof of completion — never pay a driver blind.",
      "One ledger for every job, dispute, and invoice. Exportable VAT-friendly receipts.",
      "Rate every driver. Re-book your favourites for the next move.",
    ],
    cta: "Book your first job",
  },
  drivers: {
    title: "For self-employed Luton van drivers",
    sub: "Premium removal & clearance jobs. Get paid every Friday.",
    img: "https://images.pexels.com/photos/4246119/pexels-photo-4246119.jpeg",
    imgAlt: "Self-employed van driver standing by his Luton van",
    points: [
      "High-value jobs — average £350–£900 per move, paid into your bank weekly.",
      "Job alerts straight to WhatsApp — accept with a single tap.",
      "Upload proof-of-completion photos via WhatsApp; payment auto-releases.",
      "No invoicing. No call-me-back. No middle-man cuts beyond a clear commission.",
    ],
    cta: "Apply to drive",
  },
};

export default function Audience() {
  const [tab, setTab] = React.useState("posters");
  const current = audiences[tab];

  return (
    <section
      id="audience"
      data-testid="audience-tabs"
      className="bg-[#F8FAFC] border-y border-[#0A0A0A]/5"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              Built for both sides
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
              One platform.
              <br />
              Two perspectives.
            </h2>
          </div>

          {/* Tabs */}
          <div
            role="tablist"
            className="inline-flex border border-[#0A0A0A]/15 bg-white"
          >
            {["posters", "drivers"].map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                data-testid={`audience-tab-${key}`}
                onClick={() => setTab(key)}
                className={`px-6 py-3 text-sm font-semibold tracking-tight transition-colors ${
                  tab === key
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
                }`}
              >
                {key === "posters" ? "Customers" : "Van drivers"}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch"
        >
          {/* Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/3] w-full overflow-hidden border border-[#0A0A0A]/10">
              <img
                src={current.img}
                alt={current.imgAlt}
                className="w-full h-full object-cover"
                data-testid={`audience-image-${tab}`}
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-1.5 border border-[#0A0A0A]/10 text-[11px] uppercase tracking-[0.22em] font-bold text-[#0A0A0A]">
                {tab === "posters" ? "Customer view" : "Driver view"}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-6 flex flex-col justify-center bg-white border border-[#0A0A0A]/10 p-8 md:p-12">
            <h3
              className="font-display text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A] leading-tight"
              dangerouslySetInnerHTML={{ __html: current.title }}
            />
            <p className="mt-3 text-[#475569] text-lg">{current.sub}</p>

            <ul className="mt-8 space-y-4">
              {current.points.map((p, i) => (
                <li key={i} className="flex gap-3" data-testid={`audience-point-${tab}-${i}`}>
                  <span className="shrink-0 mt-1 w-5 h-5 bg-[#1E4D55] text-white flex items-center justify-center">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span className="text-[#0A0A0A] leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>

            <a
              href="#waitlist"
              data-testid={`audience-cta-${tab}`}
              className="mt-10 group inline-flex items-center justify-between bg-[#0A0A0A] text-white hover:bg-[#1E4D55] transition-colors px-6 py-4 font-semibold w-fit"
            >
              <span>{current.cta}</span>
              <ArrowUpRight
                size={18}
                className="ml-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
